import type { Group, InstantSurvey, Question } from '@api/instant_survey/types';

import { SURVEY_SOURCE_LANGUAGE } from '@constants';
import { getOrderedSurveyLanguages } from '@helpers';

import { replaceScreenerPromptTokensWithValueMap } from '../../../pages/CreateInstantSurvey/components/Questionnaire/components/ModalScreenerQuestion/utilities';

interface TranslationEntry {
  _delete?: boolean;
  languageCode?: string;
  text?: string;
}

/**
 * Returns an item's stored translated label for the given language, or undefined to fall back to
 * the source string — when no language is selected, the language is the source language (no entry
 * exists), or the item has no translation for it.
 */
const getTranslatedLabel = (item: Item, language?: string) => {
  if (!language) return undefined;
  const translations = (item.translations ?? []) as TranslationEntry[];
  const entry = translations.find(
    ({ _delete, languageCode }) => languageCode === language && !_delete
  );
  return entry?.text || undefined;
};

export const getDropdownOptions = (items: Item[], language?: string) => {
  return items.map((item, index) => {
    const isQuotaGroup = 'name' in item;
    const sourceLabel = isQuotaGroup ? item.name : item.text;
    const label = getTranslatedLabel(item, language) ?? sourceLabel;
    const questionLabel =
      !isQuotaGroup && item.isScreener
        ? replaceScreenerPromptTokensWithValueMap(label, item.screenerValueMap)
        : label;
    return {
      label: isQuotaGroup ? label : getQuestionDropdownLabel({ index, label: questionLabel }),
      value: item.id,
    };
  });
};

export const getIsPreviewDisabled = (questions?: Question[]) => !questions?.length;

const getQuestionDropdownLabel = ({ index, label }: { index: number; label: string }) => {
  return `${index + 1}. ${label}`;
};

/**
 * Derives the ordered language list for the preview language switcher from a survey's
 * languageInfo: the source (original) language first, then each translated language (de-duped).
 * Falls back to English as the source language when languageInfo has no originalLanguage.
 */
export const getSurveyLanguages = (languageInfo?: InstantSurvey['languageInfo']) => {
  const languages = getOrderedSurveyLanguages(languageInfo);
  const [sourceLanguage] = languages;
  return { languages, sourceLanguage: sourceLanguage || SURVEY_SOURCE_LANGUAGE };
};

export type Item = Group | Question;

export interface PreviewSelection {
  language?: string;
  questionId?: number;
  quotaGroupId?: number;
  /**
   * Monotonic counter used to force a new preview session when restarting from the
   * same question and quota group selection.
   */
  restartKey?: number;
}
